import { AutoComplete, Select as AntSelect } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useMemo, useState } from 'react';
import { CSSProperties } from 'styled-components';

import { sortSuggestionsBySimilarity } from '@bodilenergy/address';
import { Address, formatAddress } from '@bodilenergy/util';
import { BreakWordWrapper } from '~src/components/BreakWordWrapper';
import { useToasters } from '~src/context/ToasterContext';
import { useAddressLookup } from '~src/hooks/useDAWA';
import { AddressWithBbrId, InputSize } from '~src/model';
import { createAddressSelector, toAddressFromDAWAAddress } from '~src/util';

const { Option } = AntSelect;

const ADDRESS_LOOKUP_FAILED = 'Adresseopslaget svarer ikke lige nu. Prøv igen om lidt.';
const ADDRESS_NOT_FOUND = 'Vi kunne ikke finde adressen. Vælg en anden adresse fra listen.';

export type AddressInputProps = {
  onChange: (address?: Partial<Address>) => void;
  onInputChange?: (s: string) => void;
  maxSuggestions?: number;
  initialValue?: Partial<Address>;
  size?: InputSize;
  style?: CSSProperties;
  placeholder?: string;
  autoFocus?: boolean;
};

export const AddressInput = ({
  maxSuggestions = 6,
  onChange,
  onInputChange,
  initialValue,
  ...rest
}: AddressInputProps) => {
  const { input, setInput, suggestions, lookupError, resetSuggestions } = useAddressLookup();
  const [suggestionsVisible, setSuggestionsVisible] = useState<boolean>(false);

  const showToaster = useToasters();
  const selector = useMemo(createAddressSelector, []);

  useEffect(() => selector.cancel, [selector]);

  useEffect(
    function showLookupError() {
      if (lookupError) {
        showToaster({ type: 'error', message: ADDRESS_LOOKUP_FAILED });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lookupError]
  );

  useEffect(() => {
    if (!isEmpty(initialValue)) {
      setInput(formatAddress(initialValue, false));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  useEffect(() => {
    if (!input) {
      resetSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const changeAddress = (address: Partial<AddressWithBbrId>) => {
    if (address) {
      setInput(formatAddress(address));
      onChange(address);
      setSuggestionsVisible(false);
    }
  };

  const visibleSuggestions = useMemo(
    () =>
      sortSuggestionsBySimilarity(suggestions, input)
        .slice(0, maxSuggestions)
        .map((suggestion, index) => ({
          value: `${index}`,
          label: suggestion.tekst.replace(/, ,/g, ','),
          suggestion,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suggestions, input]
  );

  return (
    <AutoComplete
      {...{
        value: input,
        onChange: value => {
          selector.cancel();
          onInputChange?.(value ?? '');
          setInput(value ?? '');
        },
        style: { width: '100%' },
        onSelect: async (index: number) => {
          const { label, suggestion } = visibleSuggestions[index] ?? {};

          if (!suggestion) {
            return;
          }

          setInput(label);
          onInputChange?.(label);

          const selection = await selector.select(suggestion);

          if (selection.status === 'refine') {
            setInput(selection.search);
            onInputChange?.(selection.search);
          }

          if (selection.status === 'failed') {
            showToaster({ type: 'error', message: ADDRESS_LOOKUP_FAILED });
          }

          const address = selection.status === 'resolved' ? toAddressFromDAWAAddress(selection.address) : undefined;

          if (address) {
            changeAddress(address);
          } else if (selection.status === 'resolved' || selection.status === 'not_found') {
            showToaster({ type: 'error', message: ADDRESS_NOT_FOUND });
          }
        },
        onBlur: () => setSuggestionsVisible(false),
        onFocus: () => setSuggestionsVisible(true),
        options: suggestionsVisible ? visibleSuggestions : undefined,
        ...rest,
        allowClear: true,
        open: input?.length > 0,
      }}
      data-testid='addressInput'
    >
      {visibleSuggestions.map(({ value, label }) => (
        <Option key={value} label={label}>
          <BreakWordWrapper>{label}</BreakWordWrapper>
        </Option>
      ))}
    </AutoComplete>
  );
};
